import {XIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import type React from 'react'
import type {ReactNode} from 'react'
import {useEffect, useRef} from 'react'
import {createPortal} from 'react-dom'

import {
  copilotDockedPanelClosedEvent,
  copilotDockedPanelHeaderID,
  copilotDockedPanelID,
  copilotDockedPanelReadyEvent,
} from '@github-ui/copilot-chat-helpers/constants'
import {useIsCopilotSidePanelCompact, useIsCopilotSidePanelOpen} from './copilot-side-panel-context'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {COPILOT_SIDE_PANEL_CONTENT_ID} from './copilot-side-panel-content'
import {Panel} from './panel'

interface CopilotChatSidePanelProps {
  isOpen: boolean
  onClose: () => void
  toggleSidesheetRef: React.RefObject<HTMLElement | null>
  portalTargetId?: string
  /**
   * When true, render the docked Panel inline (in place) instead of into a portal target.
   * The Dialog fallback in compact mode is unchanged.
   */
  disablePortal?: boolean
  /**
   * Optional render function for the docked Panel chrome. Receives the chat
   * body and the header-actions slot, and should return the wrapping panel
   * element. Use this when the host page needs to render its own panel chrome
   * (e.g. with a panel selector dropdown). Only used in the non-compact path;
   * the Dialog fallback in compact mode is unchanged.
   */
  renderDockedPanel?: (args: {body: ReactNode; headerActions: ReactNode}) => ReactNode
  /**
   * Optional render function for the compact-mode body. When provided, the
   * built-in compact Dialog is bypassed and consumers can wrap the body in
   * their own shell (e.g. a host-managed shared compact Dialog).
   */
  renderCompactBody?: (args: {body: ReactNode; headerActions: ReactNode}) => ReactNode
}

export function CopilotChatSidePanel({
  isOpen,
  onClose,
  toggleSidesheetRef,
  portalTargetId,
  disablePortal,
  renderDockedPanel,
  renderCompactBody,
}: CopilotChatSidePanelProps) {
  const isPanelActive = useIsCopilotSidePanelOpen()
  const targetId = portalTargetId ?? COPILOT_SIDE_PANEL_CONTENT_ID
  const sidePanelContent = isPanelActive && !disablePortal ? ssrSafeDocument?.getElementById(targetId) : null
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const isCompact = useIsCopilotSidePanelCompact()

  // Notify the copilot providers tree that the docked panel target is ready
  useEffect(() => {
    if (!isOpen) return
    document.dispatchEvent(new CustomEvent(copilotDockedPanelReadyEvent))
    return () => {
      document.dispatchEvent(new CustomEvent(copilotDockedPanelClosedEvent))
    }
  }, [isOpen])

  if (!isOpen) return null

  if (!isCompact && (sidePanelContent || disablePortal)) {
    const headerActions = <div id={copilotDockedPanelHeaderID} className="d-flex flex-items-center" />
    const body = <div id={copilotDockedPanelID} className="d-flex flex-column height-full" />
    const panel = renderDockedPanel ? (
      renderDockedPanel({body, headerActions})
    ) : (
      <Panel heading="Copilot" headerActions={headerActions}>
        <Panel.Body className="p-0">
          <Panel.ViewportFill bottomInsetPx={16}>{body}</Panel.ViewportFill>
        </Panel.Body>
      </Panel>
    )
    return sidePanelContent ? createPortal(panel, sidePanelContent) : panel
  }

  if (isCompact && renderCompactBody) {
    const headerActions = <div id={copilotDockedPanelHeaderID} className="d-flex flex-items-center" />
    const body = <div id={copilotDockedPanelID} className="d-flex flex-column height-full" />
    return <>{renderCompactBody({body, headerActions})}</>
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Copilot</Dialog.Title>
            <div className="d-flex flex-items-center">
              <div id={copilotDockedPanelHeaderID} className="d-flex flex-items-center" />
              <IconButton
                ref={closeButtonRef}
                aria-label="Close Copilot panel"
                icon={XIcon}
                variant="invisible"
                onClick={onClose}
              />
            </div>
          </div>
        </Dialog.Header>
      )}
    >
      <Panel.DialogFill>
        <div id={copilotDockedPanelID} className="d-flex flex-column height-full" />
      </Panel.DialogFill>
    </Dialog>
  )
}
